import { useEffect, useRef, useState, type ReactElement } from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { elsterApi, taxApi, type TaxHistoryEntry } from '../../lib/api';

/** Erzeugt die relevanten UStVA-Zeiträume eines Jahres je nach Rhythmus. */
function periodsFor(rhythmus: 'monthly' | 'quarterly' | 'none' | null, year: number): { value: string; label: string }[] {
  if (rhythmus === 'quarterly') {
    return [1, 2, 3, 4].map((q) => ({ value: `${year}-Q${q}`, label: `${q}. Quartal ${year}` }));
  }
  // Default + monatlich: Monate.
  const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  return months.map((m, i) => ({ value: `${year}-${String(i + 1).padStart(2, '0')}`, label: `${m} ${year}` }));
}

/**
 * Auswahlliste: aktuelles Jahr zuerst, danach das Vorjahr. Saeumige Mandanten
 * reichen die UStVA erst im Folgejahr ein — ohne die Vorjahres-Zeitraeume waere
 * das in der Oberflaeche gar nicht moeglich. Das Backend ist jahresagnostisch
 * (periodRange akzeptiert jedes YYYY-MM / YYYY-Qn).
 */
function periodOptions(rhythmus: 'monthly' | 'quarterly' | 'none' | null, year: number): { value: string; label: string; year: number }[] {
  return [
    ...periodsFor(rhythmus, year).map((o) => ({ ...o, year })),
    ...periodsFor(rhythmus, year - 1).map((o) => ({ ...o, year: year - 1 })),
  ];
}

/** Default-Zeitraum: aktueller Monat bzw. aktuelles Quartal. */
function defaultPeriod(rhythmus: 'monthly' | 'quarterly' | 'none' | null): string {
  const now = new Date();
  const y = now.getFullYear();
  if (rhythmus === 'quarterly') {
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `${y}-Q${q}`;
  }
  return `${y}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Zeitraum-Auswahl fuer die UStVA: Dropdown der relevanten Zeitraeume (Monate
 * oder Quartale je nach Rhythmus aus den Firmen-Einstellungen). Bereits
 * eingereichte Zeitraeume sind mit gruenem Haken markiert.
 */
export function UstvaPeriodPicker({
  companyId,
  period,
  setPeriod,
}: {
  companyId: string;
  period: string;
  setPeriod: (p: string) => void;
}): ReactElement {
  const [rhythmus, setRhythmus] = useState<'monthly' | 'quarterly' | 'none' | null>(null);
  const [submitted, setSubmitted] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    let alive = true;
    const year = new Date().getFullYear();
    Promise.all([
      elsterApi.overview(companyId, year).catch(() => null),
      taxApi.history(companyId, 'ustva').catch(() => [] as TaxHistoryEntry[]),
    ]).then(([ov, hist]) => {
      if (!alive) return;
      const r = ov?.ustvaRhythmus ?? null;
      setRhythmus(r);
      // Eingereicht = angenommen/übermittelt/extern eingereicht.
      const done = new Set(
        hist.filter((h) => ['accepted', 'submitted', 'extern_eingereicht'].includes(h.status)).map((h) => h.period),
      );
      setSubmitted(done);
      // Default-Zeitraum nur setzen, wenn der Nutzer noch nichts gewaehlt hat.
      setPeriod(defaultPeriod(r));
      setReady(true);
    });
    return () => { alive = false; };
    // setPeriod bewusst nicht in deps (nur einmaliges Vorbelegen).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const year = new Date().getFullYear();
  const options = periodOptions(rhythmus, year);
  const isSubmitted = submitted.has(period);

  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Zeitraum{rhythmus === 'quarterly' ? ' (Quartal)' : rhythmus === 'monthly' ? ' (Monat)' : ''}
      </span>
      <div className="flex items-center gap-2">
        {/* Custom-Dropdown: erlaubt gruenes Haekchen + Badge je Option. */}
        <div className="relative w-full" ref={ref}>
          <button
            type="button"
            disabled={!ready}
            onClick={() => setOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-xl border border-gray-300 px-4 py-2.5 text-left outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          >
            <span className="flex items-center gap-2">
              {isSubmitted && <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
              {options.find((o) => o.value === period)?.label ?? period}
            </span>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </button>
          {open && (
            <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
              {options.map((o, i) => {
                const done = submitted.has(o.value);
                const active = o.value === period;
                // Trennueberschrift, sobald ein neues Jahr beginnt.
                const newYear = i === 0 || options[i - 1]!.year !== o.year;
                return (
                  <div key={o.value}>
                  {newYear && (
                    <div className="border-t border-gray-100 px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400 first:border-t-0 dark:border-gray-700">
                      {o.year}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setPeriod(o.value); setOpen(false); }}
                    className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm ${
                      active ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="flex items-center gap-2 text-gray-800 dark:text-gray-100">
                      {done
                        ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                        : <span className="inline-block h-4 w-4 shrink-0" />}
                      {o.label}
                    </span>
                    {done && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        Eingereicht
                      </span>
                    )}
                  </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {isSubmitted && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <CheckCircle2 className="h-3.5 w-3.5" /> Eingereicht
          </span>
        )}
      </div>
      <span className="mt-1 block text-xs text-gray-400">
        Mit ✓ markierte Zeiträume wurden bereits an das Finanzamt übermittelt.
      </span>
    </label>
  );
}
